/**
 * @lodex-pro/shared — platform billing vocabulary (Phase 21 / Task 21.1, DEC-327).
 *
 * The constant lists behind Paelode Systems' own billing of its customers. Each list is
 * mirrored by a CHECK in migration 0138; the two MUST be widened together. A value here
 * with no CHECK value fails at the constraint, and a CHECK value with no line here is
 * unreachable, because the Zod enums below are built from these arrays.
 *
 * Pure data + Zod, no drizzle import, so a browser can import it without pulling the
 * schema barrel.
 */
import { z } from 'zod';

/**
 * Usage meters a rate-card line can price (21-PLAN.md section D3). Which of them are
 * actually CHARGED is a pricing decision made on screen; the list is what can be counted.
 */
export const PLATFORM_BILLING_METERS = [
  'ACTIVE_DRIVERS',
  'ACTIVE_TRUCKS',
  'ACTIVE_USERS',
  'API_CONNECTIONS',
  'API_REQUESTS',
  'MAP_LOADS',
  'HAULS_COMPLETED',
  'BBL_HAULED',
  'PORTAL_USERS',
  'DISPOSAL_MODULES',
  'COMPLIANCE_STATES',
] as const;
export type PlatformBillingMeter = (typeof PLATFORM_BILLING_METERS)[number];
export const PlatformBillingMeterSchema = z.enum(PLATFORM_BILLING_METERS);

/** How a rate-card line turns usage into money (21-PLAN.md task 21.1). */
export const PLATFORM_RATE_CARD_CHARGE_KINDS = [
  'FLAT_MONTHLY',
  'PER_UNIT',
  'TIERED_BAND',
  'PER_MODULE',
] as const;
export type PlatformRateCardChargeKind = (typeof PLATFORM_RATE_CARD_CHARGE_KINDS)[number];
export const PlatformRateCardChargeKindSchema = z.enum(PLATFORM_RATE_CARD_CHARGE_KINDS);

/** D16: every customer has a billing switch, and nobody is billed until it is ACTIVE. */
export const PLATFORM_BILLING_STATUSES = ['ACTIVE', 'NOT_BILLED', 'PAUSED'] as const;
export type PlatformBillingStatus = (typeof PLATFORM_BILLING_STATUSES)[number];
export const PlatformBillingStatusSchema = z.enum(PLATFORM_BILLING_STATUSES);

/** A version is DRAFT until published; publishing is one-way and freezes its lines. */
export const PLATFORM_RATE_CARD_VERSION_STATUSES = ['DRAFT', 'PUBLISHED'] as const;
export type PlatformRateCardVersionStatus = (typeof PLATFORM_RATE_CARD_VERSION_STATUSES)[number];

/** The tenant types Paelode bills. PLATFORM is never a customer. */
export const PLATFORM_BILLING_CUSTOMER_TYPES = ['HAULER', 'OPERATOR', 'DISPOSAL'] as const;
export type PlatformBillingCustomerType = (typeof PLATFORM_BILLING_CUSTOMER_TYPES)[number];
export const PlatformBillingCustomerTypeSchema = z.enum(PLATFORM_BILLING_CUSTOMER_TYPES);

/** Defaults a settings read returns before Paelode has saved anything (DEC-327). */
export const PLATFORM_BILLING_SETTINGS_DEFAULTS = {
  legalName: 'Paelode Systems',
  defaultTermsDays: 15,
  invoicePrefix: 'PS',
  creditNotePrefix: 'CN',
  taxRateBps: 0,
  fromAddress: 'billing@lodexpro.com',
  fromDisplayName: 'Paelode Systems Billing',
  /** Phase 21 / Task 21.2: the IANA zone that defines Paelode's local billing day. */
  billingTimezone: 'America/Chicago',
} as const;

/** Phase 21 / Task 21.6a (0232): how a payment was received (manual entry; no processor in v1). */
export const PLATFORM_PAYMENT_METHODS = ['CHECK', 'ACH', 'WIRE', 'CARD_OFFLINE', 'OTHER'] as const;
export type PlatformPaymentMethod = (typeof PLATFORM_PAYMENT_METHODS)[number];
export const PlatformPaymentMethodSchema = z.enum(PLATFORM_PAYMENT_METHODS);

/** A ledger row: money received, or the undoing of one such row (never an edit or delete). */
export const PLATFORM_PAYMENT_KINDS = ['PAYMENT', 'REVERSAL'] as const;
export type PlatformPaymentKind = (typeof PLATFORM_PAYMENT_KINDS)[number];

/** Largest logo data URI the settings row accepts (CHECK in 0138). */
export const PLATFORM_BILLING_LOGO_MAX_CHARS = 500_000;

/**
 * The logo image types an invoice can print (#940 review MED-1). One list for the settings
 * screen's file picker, the settings PUT and the invoice renderer, so an image the screen
 * accepts always prints. SVG is not one: it can carry script, and the PDF renderer leaves it out.
 */
export const PLATFORM_BILLING_LOGO_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
] as const;

/** A logo data URI the invoice renderer embeds: one of the types above, base64. */
export const PLATFORM_BILLING_LOGO_DATA_URI =
  /^data:image\/(png|jpeg|gif|webp);base64,[A-Za-z0-9+/]+={0,2}$/;

/** True when an invoice can print this logo. */
export function isPrintableLogoDataUri(v: string): boolean {
  return v.length <= PLATFORM_BILLING_LOGO_MAX_CHARS && PLATFORM_BILLING_LOGO_DATA_URI.test(v);
}

// ── Usage meters (Phase 21 / Task 21.2, PLATBILL-02, DEC-327 D3/D4) ─────────────────

/**
 * How a meter rolls up over a billing period (DEC-327 D4):
 *   - PEAK: a nightly point-in-time snapshot; the period value is the highest night.
 *   - PERIOD: recomputed from source rows for the period (hauls, BBL).
 *   - MONTHLY: an existing per-month counter, read as stored (API requests, map loads).
 */
export const PLATFORM_USAGE_METER_KINDS = ['PEAK', 'PERIOD', 'MONTHLY'] as const;
export type PlatformUsageMeterKind = (typeof PLATFORM_USAGE_METER_KINDS)[number];

export const PLATFORM_USAGE_METER_KIND: Readonly<
  Record<PlatformBillingMeter, PlatformUsageMeterKind>
> = {
  ACTIVE_DRIVERS: 'PEAK',
  ACTIVE_TRUCKS: 'PEAK',
  ACTIVE_USERS: 'PEAK',
  API_CONNECTIONS: 'PEAK',
  API_REQUESTS: 'MONTHLY',
  MAP_LOADS: 'MONTHLY',
  HAULS_COMPLETED: 'PERIOD',
  BBL_HAULED: 'PERIOD',
  PORTAL_USERS: 'PEAK',
  DISPOSAL_MODULES: 'PEAK',
  COMPLIANCE_STATES: 'PEAK',
};

/**
 * The meters shown for each customer type (D3: every meter is SHOWN; which are priced
 * is a rate-card decision). Order is display order.
 */
export const PLATFORM_USAGE_METERS_BY_CUSTOMER_TYPE: Readonly<
  Record<PlatformBillingCustomerType, readonly PlatformBillingMeter[]>
> = {
  HAULER: [
    'ACTIVE_DRIVERS',
    'ACTIVE_TRUCKS',
    'ACTIVE_USERS',
    'API_CONNECTIONS',
    'API_REQUESTS',
    'HAULS_COMPLETED',
    'BBL_HAULED',
    'MAP_LOADS',
    'COMPLIANCE_STATES',
  ],
  OPERATOR: ['ACTIVE_USERS', 'PORTAL_USERS', 'MAP_LOADS'],
  DISPOSAL: ['ACTIVE_USERS', 'DISPOSAL_MODULES', 'MAP_LOADS'],
};

/**
 * Meters with NO data source anywhere in the schema. They are never collected and are
 * reported as `collected: false` with a null value, never as zero. COMPLIANCE_STATES
 * waits on the section 51 per-state compliance add-on, which does not exist yet.
 */
export const PLATFORM_USAGE_UNCOLLECTED_METERS: readonly PlatformBillingMeter[] = [
  'COMPLIANCE_STATES',
];

/**
 * The DISPOSAL_MODULES codes, one per disposal_companies.module_* flag (DEC-005), in the
 * form a PER_MODULE rate-card line's module_code takes. platform_usage_point_in_time()
 * writes these same strings into platform_usage_daily.detail.modules.
 */
export const PLATFORM_DISPOSAL_MODULE_CODES = [
  'TICKET_VIEW',
  'DAILY_EMAIL',
  'CUSTOM_REPORTS',
  'VOLUME_TRENDS',
  'CAPTURE_RATE',
  'DIVERSION',
  'ACCEPTANCE_BY_SITE',
  'FORWARD_FORECAST',
  'DATA_EXPORT',
  'MQTT_PUBLISH',
] as const;
export type PlatformDisposalModuleCode = (typeof PLATFORM_DISPOSAL_MODULE_CODES)[number];

/** Who wrote a platform_usage_daily row, or claimed a snapshot day. */
export const PLATFORM_USAGE_SOURCES = ['SWEEPER', 'MANUAL'] as const;
export type PlatformUsageSource = (typeof PLATFORM_USAGE_SOURCES)[number];

// ── Invoices (Phase 21 / Task 21.4, PLATBILL-03 part 1, DEC-327 D1/D6/D7/D11/D12/D16) ─

/**
 * A platform invoice's life (DEC-322 shape). 21.4 writes only DRAFT; 21.5 sends (and
 * numbers, D7) and voids; 21.6 records payments. A VOID frees its period.
 */
export const PLATFORM_INVOICE_STATUSES = ['DRAFT', 'SENT', 'PAID', 'VOID'] as const;
export type PlatformInvoiceStatus = (typeof PLATFORM_INVOICE_STATUSES)[number];
export const PlatformInvoiceStatusSchema = z.enum(PLATFORM_INVOICE_STATUSES);

/**
 * Phase 21 / Task 21.5: the outcome of an invoice email. PENDING = sent (numbered) but the
 * PDF/email step has not finished; SENT = SES accepted it (a MessageId is stored); FAILED =
 * the PDF or the email failed and the invoice needs a resend.
 */
export const PLATFORM_INVOICE_EMAIL_STATUSES = ['PENDING', 'SENT', 'FAILED'] as const;
export type PlatformInvoiceEmailStatus = (typeof PLATFORM_INVOICE_EMAIL_STATUSES)[number];

/** CHARGE = priced from a rate-card line; ADJUSTMENT = a one-off line with a reason (D11). */
export const PLATFORM_INVOICE_LINE_KINDS = ['CHARGE', 'ADJUSTMENT'] as const;
export type PlatformInvoiceLineKind = (typeof PLATFORM_INVOICE_LINE_KINDS)[number];

/** MANUAL = an admin generated it; SCHEDULED = the monthly run's sweeper did. */
export const PLATFORM_INVOICE_GENERATION_SOURCES = ['MANUAL', 'SCHEDULED'] as const;
export type PlatformInvoiceGenerationSource = (typeof PLATFORM_INVOICE_GENERATION_SOURCES)[number];

/**
 * Why a customer got no draft for a period. Shown on the preview and recorded on the
 * monthly run. Listed in the order the generator checks them, so the first that
 * applies is the one reported.
 *   - NOT_ACTIVE: the billing switch is not ACTIVE (D16), or there is no account.
 *   - ALREADY_BILLED: a non-VOID invoice already covers the period (DEC-322 option A).
 *   - NO_SUBSCRIPTION: no subscription in force on the period's last day (D6).
 *   - NO_PUBLISHED_VERSION: the card has no PUBLISHED version in effect by that day.
 *   - FREE: no applicable line can ever charge (every price is 0) (D12).
 *   - USAGE_UNKNOWN: a meter a line prices was not captured (null). Never billed as 0.
 *   - NO_MATCHING_BAND: a TIERED_BAND line has no band for the meter value.
 *   - ZERO_TOTAL: the bill came to $0 (D12).
 */
export const PLATFORM_INVOICE_SKIP_REASONS = [
  'NOT_ACTIVE',
  'ALREADY_BILLED',
  'NO_SUBSCRIPTION',
  'NO_PUBLISHED_VERSION',
  'FREE',
  'USAGE_UNKNOWN',
  'NO_MATCHING_BAND',
  'ZERO_TOTAL',
] as const;
export type PlatformInvoiceSkipReason = (typeof PLATFORM_INVOICE_SKIP_REASONS)[number];

/**
 * What a preview or draft warns about, without blocking it:
 *   - USAGE_PARTIAL: a priced snapshot meter was captured on fewer days than the
 *     period has (e.g. the first month after counting started, D4).
 *   - UNKNOWN_ADD_ON: the subscription names an add-on code the version does not offer.
 *   - PERIOD_NOT_ENDED: a preview of a month that has not finished yet.
 */
export const PLATFORM_INVOICE_WARNING_CODES = [
  'USAGE_PARTIAL',
  'UNKNOWN_ADD_ON',
  'PERIOD_NOT_ENDED',
] as const;
export type PlatformInvoiceWarningCode = (typeof PLATFORM_INVOICE_WARNING_CODES)[number];
